import React, { useEffect, useMemo, useState } from "react";
import { MdTrendingUp, MdChevronLeft, MdChevronRight } from "react-icons/md";
import vMark from "../../../assets/img/auth/background-data-value.png";

/**
 * Painel "Breaking News" de Dados & IA exibido ao lado do formulário de login.
 *
 * As manchetes abaixo são editoriais (curadoria DataValue) e ficam centralizadas
 * aqui para serem facilmente trocadas por um feed real (ex.: endpoint /News ou um
 * RSS de IA/Dados) sem mexer no layout: basta alimentar `NEWS` com a mesma forma.
 */
interface NewsItem {
  category: string;
  title: string;
  summary: string;
  source: string;
  /** Minutos atrás (para o selo de tempo "há Xmin/Xh"). */
  minutesAgo: number;
}

const NEWS: NewsItem[] = [
  {
    category: "IA Generativa",
    title: "Agentes de IA passam a executar fluxos de analytics ponta a ponta",
    summary:
      "Copilots corporativos deixam de só responder perguntas e começam a orquestrar pipelines, consultas e relatórios com supervisão humana.",
    source: "DataValue Intelligence",
    minutesAgo: 8,
  },
  {
    category: "Engenharia de Dados",
    title: "Arquitetura lakehouse vira padrão de fato nas empresas de dados",
    summary:
      "Unificar data lake e data warehouse reduz custo e latência — e simplifica o caminho para casos de uso de IA em produção.",
    source: "Data Trends",
    minutesAgo: 23,
  },
  {
    category: "LLMs",
    title: "Modelos locais ganham tração por privacidade e custo previsível",
    summary:
      "Rodar LLMs on-premise ou em nuvem privada vira estratégia para dados sensíveis sem abrir mão de qualidade.",
    source: "AI Weekly",
    minutesAgo: 41,
  },
  {
    category: "Governança",
    title: "Qualidade de dados é apontada como o maior gargalo da adoção de IA",
    summary:
      "Catálogos, linhagem e contratos de dados saem do back-office e viram pré-requisito para escalar IA com confiança.",
    source: "Governance Today",
    minutesAgo: 67,
  },
  {
    category: "RAG",
    title: "RAG eleva a precisão de assistentes empresariais com dados próprios",
    summary:
      "Recuperação aumentada por geração conecta o modelo ao conhecimento da empresa e reduz alucinações em respostas críticas.",
    source: "DataValue Labs",
    minutesAgo: 95,
  },
  {
    category: "Analytics",
    title: "Métricas em tempo real impulsionam decisão orientada a dados",
    summary:
      "Streaming e observabilidade de dados encurtam o ciclo entre evento de negócio e ação — e viram diferencial competitivo.",
    source: "Insights Daily",
    minutesAgo: 132,
  },
];

const categoryChip: Record<string, string> = {
  "IA Generativa": "bg-fuchsia-500/15 text-fuchsia-200 ring-fuchsia-400/30",
  "Engenharia de Dados": "bg-sky-500/15 text-sky-200 ring-sky-400/30",
  LLMs: "bg-violet-500/15 text-violet-200 ring-violet-400/30",
  Governança: "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30",
  RAG: "bg-cyan-500/15 text-cyan-200 ring-cyan-400/30",
  Analytics: "bg-indigo-500/15 text-indigo-200 ring-indigo-400/30",
};

const timeAgo = (min: number) =>
  min < 60 ? `há ${min} min` : `há ${Math.floor(min / 60)} h`;

const ROTATE_MS = 7000;

const DataAiNewsPanel: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    []
  );

  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % NEWS.length),
      ROTATE_MS
    );
    return () => clearInterval(id);
  }, [paused]);

  const go = (dir: number) =>
    setActive((i) => (i + dir + NEWS.length) % NEWS.length);

  const featured = NEWS[active];

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-[#070710] text-white">
      {/* Fundo: marca V em alta + cúpula de luz (hero Vertex), com leve Ken Burns */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <img
          src={vMark}
          alt=""
          aria-hidden="true"
          className="animate-kenburns absolute -right-24 -top-24 h-[150%] w-auto max-w-none opacity-[0.06] blur-[1px]"
        />
        <div className="absolute inset-0 bg-[radial-gradient(55%_45%_at_90%_0%,rgba(232,121,249,0.12),transparent),radial-gradient(50%_45%_at_0%_30%,rgba(28,160,241,0.12),transparent)]" />
        <div className="nina-hero-dome absolute inset-x-0 bottom-0 h-[60%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070710]/30 via-transparent to-transparent" />
      </div>

      {/* Conteúdo */}
      <div className="relative z-10 flex h-full flex-col p-7 xl:p-9">
        {/* Cabeçalho: marca */}
        <img
          src="/datavalue-logo.svg"
          alt="DataValue"
          className="h-7 w-auto brightness-0 invert"
        />

        {/* Seção: Dados & IA */}
        <div className="mt-10">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/80">
            <MdTrendingUp className="h-4 w-4 text-emerald-400" />
            Dados &amp; IA
          </div>
          <p className="mt-1 text-xs capitalize text-white/40">{today}</p>
        </div>

        {/* Manchete em destaque (rotaciona) */}
        <div
          className="mt-8 flex flex-1 flex-col"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div key={active} className="animate-news-in flex flex-1 flex-col">
            <span
              className={`w-fit rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ring-1 ${
                categoryChip[featured.category] ??
                "bg-white/10 text-white/80 ring-white/20"
              }`}
            >
              {featured.category}
            </span>

            <h2 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight xl:text-3xl">
              {featured.title}
            </h2>

            <p className="mt-4 text-sm leading-relaxed text-white/65">
              {featured.summary}
            </p>

            <div className="mt-5 flex items-center gap-3 text-sm text-white/45">
              <span className="font-semibold text-white/70">
                {featured.source}
              </span>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <span>{timeAgo(featured.minutesAgo)}</span>
            </div>
          </div>

          {/* Controles: setas + indicadores */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Notícia anterior"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20"
              >
                <MdChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Próxima notícia"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20"
              >
                <MdChevronRight className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              {NEWS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Ir para notícia ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active ? "w-7 bg-white" : "w-2.5 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ticker "tape" inferior com todas as manchetes */}
      <div className="marquee-pause relative z-10 border-t border-white/10 bg-black/30 py-3 backdrop-blur-sm">
        <div className="flex w-max animate-marquee items-center gap-8 whitespace-nowrap px-4">
          {[...NEWS, ...NEWS].map((n, i) => (
            <span key={i} className="flex items-center gap-2 text-sm text-white/60">
              <MdTrendingUp className="h-4 w-4 shrink-0 text-emerald-400" />
              <span className="font-semibold text-white/80">{n.category}:</span>
              {n.title}
              <span className="mx-2 h-1 w-1 rounded-full bg-white/30" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataAiNewsPanel;
